import { Button, Card, Icon, Label, SelectFormField, Size, Theme, Tray } from '@busybusy/webapp-react-ui';
import { CalendarIcon } from 'assets/icons';
import classNames from 'classnames';
import { HeaderDialog } from 'components';
import Typography from 'components/foundation/text/Typography/Typography';
import OnboardingFormCoordinator, {
  IOnboardingFormField,
} from 'containers/onboarding/OnboardingFormCoordinator/OnboardingFormCoordinator';
import { useOpenable } from 'hooks';
import { Fragment, FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClassName } from 'types/ClassName';
import CompanySize from 'types/enum/CompanySize';
import { PayPeriodTypeValue } from 'types/enum/PayPeriodType';
import Trade from 'types/enum/Trade';
import { v_require } from 'utils/validations';
import OwnerOnboardingPayrollSettings, {
  IPayrollData,
} from '../OwnerOnboardingPayrollSettings/OwnerOnboardingPayrollSettings';
import './OwnerOnboardingCompanyPage.scss';

export interface IOwnerOnboardingCompanyPageProps {
  className?: ClassName;
  onCompanyFormSubmit: (formData: ICompanyOnboardingData) => void;
  onPayPeriodScheduleViewed: () => void;
  onPayPeriodScheduleChanged: () => void;
  formData: ICompanyOnboardingData;
  disableCaptcha?: boolean;
}

export interface ICompanyOnboardingData {
  companySize: CompanySize | ''; // empty string to represent empty for initial state
  trade: Trade | '';
  payrollData: IPayrollData;
}

const OwnerOnboardingCompanyPage: FunctionComponent<IOwnerOnboardingCompanyPageProps> = (props) => {
  const {
    formData,
    onCompanyFormSubmit,
    onPayPeriodScheduleViewed,
    onPayPeriodScheduleChanged,
    className,
    disableCaptcha = false,
  } = props;

  const [t] = useTranslation();
  const payPeriodScheduleOpenable = useOpenable();
  const [localFormData, setLocalFormData] = useState<ICompanyOnboardingData>(formData);
  const [payrollFormData, setPayrollFormData] = useState<IPayrollData>(formData.payrollData);
  const hasFeaturesListEnabled = true;

  async function handlePayPeriodFormSubmit(formData: IPayrollData) {
    if (formData !== localFormData.payrollData) {
      onPayPeriodScheduleChanged();
    }
    setLocalFormData({ ...localFormData, payrollData: formData });
    payPeriodScheduleOpenable.close();
  }

  function renderNumberOfEmployeesFormField(form: any, onFocus: () => void, onBlur: () => void) {
    return (
      <Fragment key="companySize">
        <Label>{t('Number of employees')}</Label>
        <SelectFormField
          name="companySize"
          form={form}
          placeholder=""
          onBlur={onBlur}
          onFocus={onFocus}
          validations={[{ validation: v_require }]}
          options={Object.values(CompanySize).map((value: string) => ({ value, label: t(value) }))}
          onChange={(value) => setLocalFormData({ ...localFormData, companySize: value })}
        />
      </Fragment>
    );
  }

  function renderTradeFormField(form: any, onFocus: () => void, onBlur: () => void) {
    return (
      <Fragment key="trade">
        <Label>{t('Trade')}</Label>
        <SelectFormField
          name="trade"
          form={form}
          placeholder=""
          onBlur={onBlur}
          onFocus={onFocus}
          validations={[{ validation: v_require }]}
          options={Object.values(Trade).map((value: string) => ({ value, label: t(value) }))}
          onChange={(value) => setLocalFormData({ ...localFormData, trade: value })}
        />
      </Fragment>
    );
  }

  function handlePayPeriodFormFieldClicked() {
    payPeriodScheduleOpenable.open();
    onPayPeriodScheduleViewed();
  }

  function renderPayPeriodScheduleFormField(form: any, onFocus: () => void, onBlur: () => void) {
    return (
      <Fragment key="payPeriodSchedule">
        <Fragment>
          <Card className="pay-period-card">
            <Tray className={'p-3'}>
              <Icon svg={CalendarIcon} size={Size.XL} theme={Theme.PRIMARY} />
              <div className="pay-period-list">
                <Label className={'mb-0 mt-2'}>{t('Pay Period Schedule')}</Label>
                <ul className="mt-2">
                  <Typography tag="h6" color="medium-gray">
                    <li className="" key={'pay-period-type'}>
                      {t(`Resets ${localFormData.payrollData.payPeriodType.toLowerCase()}`)}
                    </li>
                    <li key={'weekly-start-day'}>
                      {t(
                        `Pay period begins on ${
                          localFormData.payrollData.payPeriodType === PayPeriodTypeValue.MONTHLY
                            ? t(`the ${localFormData.payrollData.monthlyDay}`)
                            : localFormData.payrollData.payPeriodType === PayPeriodTypeValue.SEMIMONTHLY
                              ? t(
                                  `the ${localFormData.payrollData.semiMonthlyDays[0]} and ${localFormData.payrollData.semiMonthlyDays[1]}`
                                )
                              : localFormData.payrollData.weeklyStartDay
                        }`
                      )}
                    </li>
                    <li key={'overtime-start'}>
                      {t(`Overtime week begins on ${localFormData.payrollData.overtimeStart}`)}
                    </li>
                  </Typography>
                </ul>
              </div>
              <Button type="link" onClick={handlePayPeriodFormFieldClicked}>
                {t('Edit')}
              </Button>
            </Tray>
          </Card>
        </Fragment>
        <HeaderDialog
          isOpen={payPeriodScheduleOpenable.isOpen}
          onClose={() => {
            setPayrollFormData(localFormData.payrollData);
            payPeriodScheduleOpenable.close();
          }}
          title={t('Edit Pay Period Schedule')}
          divider={false}
        >
          <OwnerOnboardingPayrollSettings
            onPayPeriodFormSubmit={handlePayPeriodFormSubmit}
            submitButtonText={t('Submit')}
            formData={payrollFormData}
            setFormData={setPayrollFormData}
            renderLeftContent={false}
            className={'px-8 pb-8'}
          />
        </HeaderDialog>
      </Fragment>
    );
  }

  function getFormFields(): IOnboardingFormField[] {
    return [
      {
        panelContent: {
          bodyText: t('Select the range that roughly describes the size of your company.'),
          headerText: t('How many employees are in your company?'),
          isTitle: false,
        },
        renderField: renderNumberOfEmployeesFormField,
      },
      {
        panelContent: {
          bodyText: t("Select the trade that most closely represents your company's work."),
          headerText: t('What is the primary trade for your company?'),
          isTitle: false,
        },
        renderField: renderTradeFormField,
      },
      {
        panelContent: {
          bodyText: '',
          headerText: '',
          isTitle: false,
        },
        renderField: renderPayPeriodScheduleFormField,
      },
    ];
  }

  const classes = classNames('owner-onboarding-company-page', className);

  return (
    <OnboardingFormCoordinator
      defaultHeader={t('Company')}
      defaultBody={t('To get started, please tell us about your company.')}
      formFields={getFormFields()}
      formData={localFormData}
      formButtonLabel={hasFeaturesListEnabled ? t('Next step') : t('Finish')}
      onSubmit={onCompanyFormSubmit}
      className={classes}
      disableCaptcha={disableCaptcha}
    />
  );
};

export default OwnerOnboardingCompanyPage;
